/* eslint-disable @typescript-eslint/no-explicit-any */
import { Colors } from '@back-office/core';
import { useLocalStore } from 'mobx-react';
import { BaseSyntheticEvent, useState } from 'react';
import {
  Control,
  DeepMap,
  FieldError,
  FieldValues,
  SubmitErrorHandler,
  SubmitHandler,
  UnpackNestedValue,
  useForm,
} from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { useMount, useSearchParam } from 'react-use';
import { InvitationProps } from '.';
import { ResponseMessage } from '../../constants';
import {
  InvitationFieldNames,
  InvitationForm,
  InvitationStep,
  LoginStep,
} from '../../forms';
import useInject from '../../hooks/useInject';

type FormState = {
  isHasAtLeast8: boolean;
  isHasLowerCase: boolean;
  isHasUpperCase: boolean;
  isHasNumber: boolean;
  isHasSpecialCharacter: boolean;
  password: string;
  readonly isValid: boolean;
  handleAtLeast(value?: string): keyof Colors;
  handleLowerCase(value?: string): keyof Colors;
  handleUpperCase(value?: string): keyof Colors;
  handleContainNumber(value?: string): keyof Colors;
  handleSpecialCharacter(value?: string): keyof Colors;
};

type UseViewModelReturn = {
  control: Control<InvitationForm>;
  step: InvitationStep;
  errors: DeepMap<InvitationForm, FieldError>;
  state: FormState;
  handleSubmit: <TSubmitFieldValues extends FieldValues = InvitationForm>(
    onValid: SubmitHandler<TSubmitFieldValues>,
    onInvalid?: SubmitErrorHandler<InvitationForm> | undefined
  ) => (e?: BaseSyntheticEvent) => Promise<void>;
  watchPassword: UnpackNestedValue<
    InvitationForm[InvitationFieldNames.Password]
  >;
  watchConfirmPassword: UnpackNestedValue<
    InvitationForm[InvitationFieldNames.ConfirmPassword]
  >;
  handleOnSubmit: (data: InvitationForm) => Promise<void>;
  merchantName: string | null;
  goToLoginPage: () => void;
  goToEnterOtpPage: () => Promise<void>;
  email: string | null;
};

const useViewModel = ({
  isForgotPass,
}: InvitationProps): UseViewModelReturn => {
  const { control, errors, watch, handleSubmit } = useForm<InvitationForm>({
    mode: 'onSubmit',
  });
  const history = useHistory();
  const {
    checkInvitation,
    newPassword,
    checkForgotPassword,
    alert,
    setLoginStep,
    requestRegisterOTP,
  } = useInject((store) => store);
  const email = useSearchParam('email');
  const token = useSearchParam('token') as string;
  const merchantName = useSearchParam('merchantName');
  const [step, setStep] = useState<InvitationStep>(InvitationStep.Checking);
  const watchPassword = watch(InvitationFieldNames.Password);
  const watchConfirmPassword = watch(InvitationFieldNames.ConfirmPassword);

  const state = useLocalStore(() => ({
    isHasAtLeast8: false,
    isHasLowerCase: false,
    isHasUpperCase: false,
    isHasNumber: false,
    isHasSpecialCharacter: false,
    password: '',
    get isValid(): boolean {
      return (
        this.isHasAtLeast8 &&
        this.isHasLowerCase &&
        this.isHasUpperCase &&
        this.isHasNumber &&
        this.isHasSpecialCharacter
      );
    },
    handleAtLeast(value = ''): keyof Colors {
      const regex = /.{8,}/;
      this.isHasAtLeast8 = regex.test(value);
      return regex.test(value) ? 'deYork' : 'hitGray';
    },
    handleLowerCase(value = ''): keyof Colors {
      const regex = /[a-z]/;
      this.isHasLowerCase = regex.test(value);
      return regex.test(value) ? 'deYork' : 'hitGray';
    },
    handleUpperCase(value = ''): keyof Colors {
      const regex = /[A-Z]/;
      this.isHasUpperCase = regex.test(value);
      return regex.test(value) ? 'deYork' : 'hitGray';
    },
    handleContainNumber(value = ''): keyof Colors {
      const regex = /\d/;
      this.isHasNumber = regex.test(value);
      return regex.test(value) ? 'deYork' : 'hitGray';
    },
    handleSpecialCharacter(value = ''): keyof Colors {
      const regex = /[^A-Za-z0-9]/;
      this.isHasSpecialCharacter = regex.test(value);
      return regex.test(value) ? 'deYork' : 'hitGray';
    },
  }));

  useMount(async () => {
    if (!token) {
      history.push('/login');
    }
    const query = isForgotPass ? checkForgotPassword : checkInvitation;
    const message = await query({ token }, () => {
      history.push('/login');
      alert.closeDialog();
    });
    handleOnSuccess(message);
  });

  const handleOnSubmit = async (data: InvitationForm): Promise<void> => {
    if (isForgotPass) {
      await newPassword({ token: token || '', password: data.password }, () => {
        setLoginStep(LoginStep.Login);
        history.push('/login');
        alert.closeDialog();
      });
    } else {
      state.password = data.password;
    }
    setStep(InvitationStep.Success);
  };

  const goToLoginPage = (): void => {
    setLoginStep(LoginStep.Login);
    history.push('/login');
  };

  const goToEnterOtpPage = async (): Promise<void> => {
    setLoginStep(LoginStep.EnterOTP);
    await requestRegisterOTP({
      token,
      password: state.password,
      email: email || '',
    });
    history.push('/login', {
      token,
      password: state.password,
      isRegister: true,
    });
  };

  const handleOnSuccess = (message: string): void => {
    if (message === ResponseMessage.OK) {
      setStep(InvitationStep.Register);
    } else if (message === InvitationStep.Duplicate) {
      setStep(InvitationStep.Duplicate);
    }
  };

  return {
    control,
    step,
    errors,
    state,
    handleSubmit,
    watchPassword,
    watchConfirmPassword,
    handleOnSubmit,
    merchantName,
    goToLoginPage,
    goToEnterOtpPage,
    email,
  };
};

export default useViewModel;
